import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWrapper from "../utils/httpClient";
import { API_GET_PRODUCT, API_UPDATE_PRODUCT } from "../utils/apis";
import { API_STATE } from "../utils/constant";

// Asynchronous thunk for fetching resources by content type
export const fetchProduct = createAsyncThunk(
  "product/fetchProduct",
  async ({ productId, language }) => {
    try {
      const response = await fetchWrapper.get(
        `${API_GET_PRODUCT}/${productId}/${language}`
      );

      const contentData = response.resdata.contentData || {};
      const topIntroduction = {
        ...(contentData.topIntroduction || {}),
        ...(contentData.poweredByLabel && { poweredByLabel: contentData.poweredByLabel }),
        ...(contentData.theme && { theme: contentData.theme }),
        ...(contentData.videoIntroduction && { videoIntroduction: contentData.videoIntroduction }),
        ...(contentData.iconFileId && { iconFileId: contentData.iconFileId }),
        ...(contentData.bannerFileId && { bannerFileId: contentData.bannerFileId }),
      };

      const transformedData = {
        topIntroduction,
        plans: contentData.plans || {},
        campaign: contentData.campaign || {},
        faq: contentData.faq || {},
        productSpec: contentData.productSpec || {},
        commonIssue: contentData.commonIssue || {},
        hardware: contentData.hardware || {},
        capabilities: contentData.capabilities || {},
        customerStories: contentData.customerStories || {},
        brands: contentData.brands || {},
        otherProducts: contentData.otherProducts || {},
        region: response.resdata.region,
        language: response.resdata.language,
        languages: response.resdata.languages || [],
        uuid: response.resdata.uuid
      };

      return transformedData;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  "product/updateProduct",
  async ({ uuid, language, payload }) => {
    try {
      const response = await fetchWrapper.put(
        `${API_UPDATE_PRODUCT}/${uuid}/${language}`,
        payload
      );
      return response;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const initialPlans = {
  title: "",
  description: "",
  tableHead: "",
  bottomDesc: "",
  pricePlans: [{
    topTitle: "",
    topTitleColor: "",
    planTitle: "",
    planSubtitle: "",
    pricing: "",
    priceNote: "",
    promoType: "",
    promoDesc: "",
    remarkPricing: "",
    planDescription: "",
    benefits: [
      {
        title: "",
        desc: "",
        icon: ""
      }
    ]
  }],
};


export const initialFaq = {
  title: "",
  items: [{
    question: "",
    answer: ""
  }],
};

export const initialSpecInfo = {
  title: "",
  prodSpecHeader: "",
  items: [{
    title: "",
    iconUrl: '',
    iconFileId: '',
    description: "",
    width: 72,
    height: 72
  }]
};

export const initialCommonIssue = {
  title: "",
  items: [{
    title: "",
    description: "",
    title1: "",
    description1: ""
  }]
};

export const initialHardware = {
  title: "",
  items: [{
    title: "",
    desc: "",
    imageUrl: "",
    imageFileId: "",
    width: 275,
    height: 275
  }]
};

export const initialCapabilities = {
  title: "",
  description: "",
  items: [{
    title: "",
    description: "",
    media: {
      mediaType: "",
      mediaUrl: "",
      mediaFileId: "",
    }
  }]
};

export const initialCustomerStories = {
  title: "",
  items: [{
    logo: "",
    logoFileId: "",
    stories: "",
    name: "",
    position: "",
    company: "",
    borderColor: ""
  }]
};

export const initialBrands = {
  title: "",
  description: "",
  src: [{
    iconUrl: "",
    iconFileId: '',
    description: "",
    width: 64,
    height: 64
  }]
};

export const initialOtherProducts = {
  title: "",
  items: [{
    title: '',
    description: '',
    url: ''
  }]
};

const initialProduct = {
  topIntroduction: {
    menuName: "",
    menuDescription: "",
    subMenuName: "",
    iconUrl: "",
    iconFileId: "",
    title: "",
    description: "",
    bannerUrl: "",
    bannerFileId: "",
    poweredByLabel: "",
    theme: "",
    videoIntroduction: "",
  },

  campaign: {
    campaigns: [{
      offerType: '',
      campaignTitle: '',
      campaignSubtitle: '',
      campaignImageFileId: '',
      campaignImageUrl: '',
      benefits: [{
        title: "",
        description: ""
      }]
    }]
  },
  plans: initialPlans,
  faq: initialFaq,
  productSpec: initialSpecInfo,
  commonIssue: initialCommonIssue,
  hardware: initialHardware,
  capabilities: initialCapabilities,
  customerStories: initialCustomerStories,
  brands: initialBrands,
  otherProducts: initialOtherProducts,
  region: "SG",
  languages: [],
};

// Slice
const productSlice = createSlice({
  name: "product",
  initialState: {
    data: null,
    status: API_STATE.IDLE,
    error: null,
  },
  reducers: {
    resetProductValue: (state, action) => {
      const { payload } = action;
      state.data = { ...initialProduct, languages: [payload.language] };
      state.status = API_STATE.SUCCEEDED;
    },
    setValueBasicInfo: (state, action) => {
      const { payload } = action;

      if (state.data) {
        if (!state.data.topIntroduction) {
          state.data.topIntroduction = {};
        }
        state.data.topIntroduction[payload.key] = payload.value;
      }
    },
    setValuePlans: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.plans = payload.value;
      } else if (state.data) {
        state.data.plans = payload.value;
      }
    },
    setValueCampaigns: (state, action) => {
      const { payload } = action;
      if (state.data) {
        if (state.data.campaign) {
          state.data.campaign.campaigns = payload.value;
        }
        else if (state.data.contentData?.campaign) {
          state.data.contentData.campaign.campaigns = payload.value;
        }
        else {
          if (state.data.contentData) {
            if (!state.data.contentData.campaign) {
              state.data.contentData.campaign = { campaigns: [] };
            }
            state.data.contentData.campaign.campaigns = payload.value;
          } else {
            if (!state.data.campaign) {
              state.data.campaign = { campaigns: [] };
            }
            state.data.campaign.campaigns = payload.value;
          }
        }
      }
    },
    setValueFaqs: (state, action) => {
      const { payload } = action;

      if (state.data) {
        if (state.data.contentData) {
          if (!state.data.contentData.faq) {
            state.data.contentData.faq = initialFaq;
          }
          state.data.contentData.faq = {
            ...state.data.contentData.faq,
            ...payload.value
          };
        } else {
          if (!state.data.faq) {
            state.data.faq = initialFaq;
          }
          state.data.faq = {
            ...state.data.faq,
            ...payload.value
          };
        }
      }
    },
    setValueSpecInfo: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.productSpec = payload.value;
      } else {
        state.data.productSpec = payload.value;
      }
    },
    setValueCommonIssue: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.commonIssue = payload.value;
      } else {
        state.data.commonIssue = payload.value;
      }
    },
    setValueHardware: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.hardware = payload.value;
      } else {
        state.data.hardware = payload.value;
      }
    },
    setValueCapabilities: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.capabilities = payload.value;
      } else {
        state.data.capabilities = payload.value;
      }
    },
    setValueCustomerStories: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.customerStories = payload.value;
      } else {
        state.data.customerStories = payload.value;
      }
    },
    setValueBrands: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.brands = payload.value;
      } else {
        state.data.brands = payload.value;
      }
    },
    setValueOtherProducts: (state, action) => {
      const { payload } = action;

      if (state.data.contentData) {
        state.data.contentData.otherProducts = payload.value;
      } else {
        state.data.otherProducts = payload.value;
      }
    },
    setError: (state, action) => {
      const { payload } = action;
      state.error = { ...payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetch product
      .addCase(fetchProduct.pending, (state) => {
        state.error = null;
        state.status = API_STATE.LOADING;
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.data = action.payload;
        state.status = API_STATE.SUCCEEDED;
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = API_STATE.FAILED;
      })

      // Handle update product
      .addCase(updateProduct.pending, (state) => {
        state.error = null;
        state.status = API_STATE.LOADING;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.status = API_STATE.SUCCEEDED;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = API_STATE.FAILED;
      });
  },
});

//Action creators are generated for each case reducer function
export const {
  resetProductValue,
  setValueBasicInfo,
  setValuePlans,
  setValueCampaigns,
  setValueFaqs,
  setValueSpecInfo,
  setValueCommonIssue,
  setValueHardware,
  setValueCapabilities,
  setValueCustomerStories,
  setValueBrands,
  setValueOtherProducts,
  setError,
} = productSlice.actions;

// Export the async action so it can be dispatched from components
export default productSlice.reducer;
